import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LtmsLoaderComponent } from '../../../../shared/components/ltms-loader/ltms-loader.component';

export type OutboxChannelChipStatus = 'selectable' | 'sent' | 'delivered' | 'not_selected';

export interface OutboxChannelOption {
  key: string;
  label: string;
  selected: boolean;
  status: OutboxChannelChipStatus;
}

export interface OutboxTrialSection {
  trialNumber: number;
  title: string;
  enabled: boolean;
  completed: boolean;
  selectAll: boolean;
  expanded: boolean;
  sentDate: string | null;
  managerEmail: string | null;
  channels: OutboxChannelOption[];
}

@Component({
  selector: 'app-outbox-channel-selection-panel',
  templateUrl: './outbox-channel-selection-panel.component.html',
  styleUrl: './outbox-channel-selection-panel.component.scss',
  imports: [NgIf, NgFor, FormsModule, LtmsLoaderComponent]
})
export class OutboxChannelSelectionPanelComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() isLoading = false;
  @Input() isSending = false;
  @Input() onDemandConfigurations: any = null;
  @Input() requestTrials: any[] = [];
  /** Selected CFR request. Channels without a usable contact value are hidden. */
  @Input() request: any = null;
  /** When search uses a tag_* field, show label+value from the selected row. */
  @Input() tagSearchContext: { label: string; value: string } | null = null;
  /** Absolute link expiry time used for the live Request Deadline countdown. */
  @Input() requestDeadlineAt: Date | number | string | null = null;
  /** Pre-formatted absolute expiry, e.g. "05 Sep '26 14:30". */
  @Input() requestDeadlineAbsolute: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() send = new EventEmitter<{ trialNumber: number; channels: string[] }>();
  @Output() viewDetails = new EventEmitter<void>();

  readonly channelMeta: { key: string; label: string }[] = [
    { key: 'phone', label: 'SMS' },
    { key: 'email', label: 'Email' },
    { key: 'whatsapp', label: 'WhatsApp' }
  ];

  readonly channelContactFields: Record<string, string> = {
    phone: 'tag_private_phone_number',
    email: 'tag_private_email_address',
    whatsapp: 'tag_private_whatsapp_number'
  };

  trialSections: OutboxTrialSection[] = [];
  activeTrialNumber = 1;
  liveDeadlineLabel: string | null = null;
  showDeadlineAsDate = false;
  private deadlineTimerId: ReturnType<typeof setInterval> | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['onDemandConfigurations'] || changes['requestTrials'] || changes['request'] || changes['visible']) {
      const resetExpanded = changes['visible']?.currentValue === true;
      this.buildTrialSections(resetExpanded);
    }
    if (changes['visible']?.currentValue === true) {
      this.showDeadlineAsDate = false;
    }
    if (changes['requestDeadlineAt'] || changes['visible']) {
      this.syncDeadlineTimer();
    }
  }

  ngOnDestroy(): void {
    this.stopDeadlineTimer();
  }

  get showDeadlineBlock(): boolean {
    return !!(this.liveDeadlineLabel || this.requestDeadlineAbsolute);
  }

  get isDeadlineExpired(): boolean {
    return this.liveDeadlineLabel === 'Expired';
  }

  toggleDeadlineDisplay(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.requestDeadlineAbsolute && !this.showDeadlineAsDate) {
      return;
    }
    this.showDeadlineAsDate = !this.showDeadlineAsDate;
  }

  syncDeadlineTimer() {
    this.stopDeadlineTimer();
    this.refreshDeadlineLabel();

    if (!this.visible || !this.requestDeadlineAt || this.liveDeadlineLabel === 'Expired') {
      return;
    }

    this.deadlineTimerId = setInterval(() => {
      this.refreshDeadlineLabel();
      if (this.liveDeadlineLabel === 'Expired') {
        this.stopDeadlineTimer();
      }
    }, 1000);
  }

  stopDeadlineTimer() {
    if (this.deadlineTimerId != null) {
      clearInterval(this.deadlineTimerId);
      this.deadlineTimerId = null;
    }
  }

  refreshDeadlineLabel() {
    this.liveDeadlineLabel = this.formatRemainingDeadline(this.requestDeadlineAt);
  }

  formatRemainingDeadline(deadlineAt: Date | number | string | null): string | null {
    if (deadlineAt == null || deadlineAt === '') {
      return null;
    }
    const expiryMs = new Date(deadlineAt).getTime();
    if (Number.isNaN(expiryMs)) {
      return null;
    }

    const remainingMs = expiryMs - Date.now();
    if (remainingMs <= 0) {
      return 'Expired';
    }

    const dayMs = 24 * 60 * 60 * 1000;
    const hourMs = 60 * 60 * 1000;
    const minMs = 60 * 1000;
    const secMs = 1000;

    const days = Math.floor(remainingMs / dayMs);
    const hours = Math.floor((remainingMs % dayMs) / hourMs);
    const mins = Math.floor((remainingMs % hourMs) / minMs);
    const secs = Math.floor((remainingMs % minMs) / secMs);

    const parts: string[] = [];
    if (days > 0) {
      parts.push(`${days}d`);
    }
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    if (mins > 0) {
      parts.push(`${mins}m`);
    }
    parts.push(`${secs}s`);
    return parts.join(' ');
  }

  buildTrialSections(resetExpanded = false) {
    const onDemandConfig = this.onDemandConfigurations || {};
    const fallbackChannels = Array.isArray(onDemandConfig.communication_channels)
      ? onDemandConfig.communication_channels
      : [];
    const trialParams = onDemandConfig.trial_params || {};
    const maxAttempts = this.getMaxOnDemandAttempts(onDemandConfig);
    const maxCompleted = this.getMaxCompletedOnDemandAttempt(this.requestTrials || []);
    this.activeTrialNumber = Math.min(maxCompleted + 1, maxAttempts);

    const previousByTrial: Record<number, OutboxTrialSection> = {};
    (this.trialSections || []).forEach((section) => {
      previousByTrial[section.trialNumber] = section;
    });

    const sections: OutboxTrialSection[] = [];
    for (let trialNumber = 1; trialNumber <= maxAttempts; trialNumber++) {
      const trialChannelKeys = this.getTrialChannels(trialParams, trialNumber, fallbackChannels);
      const completed = trialNumber <= maxCompleted;
      const enabled = trialNumber === this.activeTrialNumber && !completed && trialNumber <= maxAttempts;
      const previous = previousByTrial[trialNumber];
      const channels = completed
        ? this.mapCompletedChannelOptions(trialChannelKeys, trialNumber)
        : this.mapSelectableChannelOptions(trialChannelKeys, previous?.channels, enabled);
      const expanded = (!resetExpanded && previous) ? previous.expanded : enabled;

      sections.push({
        trialNumber,
        title: `Trial ${trialNumber}`,
        enabled,
        completed,
        selectAll: channels.length > 0 && channels.every((channel) => channel.selected),
        expanded,
        sentDate: completed ? this.getSentDateForTrial(trialNumber) : null,
        managerEmail: completed ? this.getManagerEmailForTrial(trialNumber) : null,
        channels
      });
    }
    this.trialSections = sections;
  }

  mapSelectableChannelOptions(
    channelKeys: string[],
    previousChannels?: OutboxChannelOption[],
    isActiveTrial: boolean = false
  ): OutboxChannelOption[] {
    const allowed = new Set(
      (channelKeys || []).map((key) => this.normalizeChannelKey(key)).filter(Boolean)
    );
    const channels = this.channelMeta
      .filter((meta) => allowed.has(meta.key) && this.hasContactForChannel(meta.key))
      .map((meta) => {
        const previous = previousChannels?.find((item) => item.key === meta.key);
        return {
          key: meta.key,
          label: meta.label,
          selected: previous ? previous.selected : true,
          status: 'selectable' as OutboxChannelChipStatus
        };
      });

    // Active trial with only one configured channel → always selected by default.
    if (isActiveTrial && channels.length === 1) {
      channels[0].selected = true;
    }

    return channels;
  }

  mapCompletedChannelOptions(channelKeys: string[], trialNumber: number): OutboxChannelOption[] {
    const allowed = new Set(
      (channelKeys || []).map((key) => this.normalizeChannelKey(key)).filter(Boolean)
    );
    const trialRecord = this.getOnDemandTrialRecord(trialNumber);
    const trialChannels = Array.isArray(trialRecord?.channels) ? trialRecord.channels : [];

    // Always include channels present on the CFR trial (e.g. delivered SMS),
    // even if naming differs slightly from config keys.
    trialChannels.forEach((channel: any) => {
      const key = this.normalizeChannelKey(channel?.channel);
      if (key) {
        allowed.add(key);
      }
    });

    return this.channelMeta
      .filter((meta) => allowed.has(meta.key) && this.hasContactForChannel(meta.key))
      .map((meta) => {
        const match = trialChannels.find((channel: any) => {
          return this.normalizeChannelKey(channel?.channel) === meta.key;
        });
        return {
          key: meta.key,
          label: meta.label,
          selected: false,
          status: this.resolveCompletedChannelStatus(match)
        };
      });
  }

  hasContactForChannel(channelKey: string): boolean {
    const field = this.channelContactFields[channelKey];
    if (!field) {
      return true;
    }
    const value = this.request?.[field];
    if (value == null) {
      return false;
    }
    const text = String(value).trim();
    if (!text) {
      return false;
    }
    const normalized = text.toLowerCase();
    return normalized !== 'na' && normalized !== 'n/a' && normalized !== 'null' && normalized !== 'undefined';
  }

  normalizeChannelKey(value: any): string {
    const key = String(value || '').toLowerCase().trim();
    if (!key) {
      return '';
    }
    if (key === 'sms' || key === 'phone' || key === 'mobile' || key === 'phone_number') {
      return 'phone';
    }
    if (key === 'mail' || key === 'email_address' || key === 'e-mail') {
      return 'email';
    }
    if (key === 'wa' || key === 'whatsapp' || key === 'whats_app') {
      return 'whatsapp';
    }
    return key;
  }

  resolveCompletedChannelStatus(channel: any): OutboxChannelChipStatus {
    if (!channel) {
      return 'not_selected';
    }

    const sent = this.isTruthyFlag(channel.sent);
    const delivered =
      this.isTruthyFlag(channel.delivered) ||
      Number(channel.delivery_status) === 1;
    const attempted =
      this.isTruthyFlag(channel.attempted) ||
      sent ||
      delivered ||
      !!channel.sent_date ||
      !!channel.delivery_date ||
      !!channel.message_id;

    if (delivered) {
      return 'delivered';
    }
    if (sent || attempted) {
      return 'sent';
    }
    return 'not_selected';
  }

  isTruthyFlag(value: any): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  getOnDemandTrialRecord(trialNumber: number): any {
    const attemptKey = `on_demand_${trialNumber}`;
    return (this.requestTrials || []).find((trial) => String(trial?.attempt || '') === attemptKey);
  }

  getSentDateForTrial(trialNumber: number): string | null {
    const match = this.getOnDemandTrialRecord(trialNumber);
    const attemptDate = match?.attempt_date;
    if (attemptDate == null || attemptDate === '' || attemptDate === 'N/A' || attemptDate === 'NA') {
      return null;
    }
    return String(attemptDate);
  }

  getManagerEmailForTrial(trialNumber: number): string | null {
    const match = this.getOnDemandTrialRecord(trialNumber);
    const email = match?.manager_email;
    if (email == null || email === '' || email === 'N/A' || email === 'NA') {
      return null;
    }
    return String(email);
  }

  getMaxOnDemandAttempts(onDemandConfig: any): number {
    const fromAttempts = parseInt(onDemandConfig?.trial_params?.on_demand_attempts, 10);
    if (!Number.isNaN(fromAttempts) && fromAttempts > 0) {
      return fromAttempts;
    }
    const allowed = parseInt(onDemandConfig?.allowed_attempts, 10);
    if (!Number.isNaN(allowed) && allowed > 0) {
      return allowed;
    }
    const trialParams = onDemandConfig?.trial_params || {};
    let count = 0;
    Object.keys(trialParams).forEach((key) => {
      const match = /^trial_(\d+)$/.exec(key);
      if (match) {
        count = Math.max(count, parseInt(match[1], 10));
      }
    });
    return count > 0 ? count : 1;
  }

  getMaxCompletedOnDemandAttempt(trials: any[]): number {
    let max = 0;
    (trials || []).forEach((trial) => {
      const attempt = String(trial?.attempt || '');
      if (attempt.indexOf('on_demand_') !== 0) {
        return;
      }
      const n = parseInt(attempt.replace('on_demand_', ''), 10);
      if (!Number.isNaN(n) && n > max) {
        max = n;
      }
    });
    return max;
  }

  getTrialChannels(trialParams: any, trialNumber: number, fallbackChannels: string[]): string[] {
    const configured = trialParams?.[`trial_${trialNumber}`]?.params?.communication_channels;
    if (Array.isArray(configured) && configured.length) {
      return configured;
    }
    return fallbackChannels;
  }

  toggleTrialExpanded(section: OutboxTrialSection, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    section.expanded = !section.expanded;
  }

  onToggleChannel(section: OutboxTrialSection) {
    if (!section.enabled || this.isSending || section.completed) {
      return;
    }
    section.selectAll = section.channels.length > 0 && section.channels.every((channel) => channel.selected);
  }

  getSelectedChannels(section: OutboxTrialSection): string[] {
    return section.channels.filter((channel) => channel.selected).map((channel) => channel.key);
  }

  canSendTrial(section: OutboxTrialSection): boolean {
    return section.enabled
      && !this.isLoading
      && !this.isSending
      && !this.isDeadlineExpired
      && this.getSelectedChannels(section).length > 0;
  }

  closePanel() {
    if (this.isSending) {
      return;
    }
    this.close.emit();
  }

  openRequestDetails(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.isSending) {
      return;
    }
    this.viewDetails.emit();
  }

  sendTrial(section: OutboxTrialSection) {
    if (!this.canSendTrial(section)) {
      return;
    }
    this.send.emit({
      trialNumber: section.trialNumber,
      channels: this.getSelectedChannels(section)
    });
  }
}
